import { type Accessor, createSignal, type Setter } from "solid-js";

export type PaginationProps = {
	page: Accessor<number>;
	pageSize: Accessor<number>;
	onPageChange: (page: number) => void;
	onItemsPerPageChange: (pageSize: number) => void;
};

type SortProps = {
	sort: Accessor<string | undefined>;
	asc: Accessor<boolean | undefined>;
	setSort: (sort: string, defaultAsc?: boolean) => void;
};
export type PaginationState = {
	setSearch: Setter<string>;
	searchInput: Accessor<string>;
	setSearchInput: Setter<string>;
	page: Accessor<number>;
	setPage: (value: number) => void;
	pageSize: Accessor<number>;
	setPageSize: (value: number) => void;
	updateQueryString: (additionalParams?: Record<string, string>) => void;
	getQueryState: () => {
		search: string;
		page: number;
		pageSize: number;
	};
	searchProps: {
		value: string;
		onSearch: (search: string) => void;
		onClear: () => void;
		getQueryState: () => {
			search: string;
			page: number;
			pageSize: number;
		};
	};
	paginationProps: PaginationProps;
	sortProps: SortProps;
};

/**
 * Reusable composable for pagination and search state management.
 * Handles URL query parameters, debounced search, and query string updates.
 *
 * @param options - Configuration options
 * @param options.defaultSearch - Default search value (default: "")
 * @param options.defaultPage - Default page number (default: 1); omitted from the URL when current page matches
 * @param options.defaultPageSize - Default page size (default: 10); omitted from the URL (`pagesize` param) when current size matches
 * @param options.debounceMs - Debounce delay for search in milliseconds (default: 300)
 * @param options.onUpdate - Optional callback when query string is updated
 * @param options.queryStringPrefix - Optional prefix for URL query keys (e.g. `boards_main` yields
 *   `boards_main_search`, `boards_main_page`, …). Use when several paginated lists share a page.
 * @returns Pagination state and handlers
 */
export function usePagination(
	options: {
		defaultSearch?: string;
		defaultPage?: number;
		defaultPageSize?: number;
		defaultSort?: string;
		defaultAsc?: boolean;
		debounceMs?: number;
		queryStringPrefix?: string;
		onUpdate?: (queryState: {
			search: string;
			page: number;
			pageSize: number;
		}) => void;
	} = {},
): PaginationState {
	const {
		defaultSearch = "",
		defaultPage = 1,
		defaultPageSize = 10,
		debounceMs = 300,
		onUpdate,
		defaultSort = undefined,
		defaultAsc = undefined,
		queryStringPrefix,
	} = options;

	const prefixedKey = (
		base: "search" | "page" | "pagesize" | "sort" | "asc",
	): string =>
		queryStringPrefix !== undefined && queryStringPrefix !== ""
			? `${queryStringPrefix}_${base}`
			: base;

	/** When a default sort column is set, treat ascending as the default sort direction for URL sync. */
	const urlDefaultAsc =
		defaultAsc ?? (defaultSort !== undefined ? true : undefined);

	// Get initial values from URL parameters
	const parameters = new URLSearchParams(window.location.search);
	const initialSearch = parameters.get(prefixedKey("search")) ?? defaultSearch;
	const initialSort = parameters.get(prefixedKey("sort")) ?? defaultSort;
	const ascParam = parameters.get(prefixedKey("asc"));
	const initialAsc = ascParam !== null ? ascParam === "true" : urlDefaultAsc;
	const pageKey = prefixedKey("page");
	const initialPage = parameters.get(pageKey)
		? parseInt(parameters.get(pageKey) ?? String(defaultPage), 10)
		: defaultPage;
	const pageSizeParamKey = prefixedKey("pagesize");
	const pageSizeParam = parameters.get(pageSizeParamKey);
	const initialPageSize = pageSizeParam
		? parseInt(pageSizeParam, 10)
		: defaultPageSize;
	const [searchInput, setSearchInput] = createSignal(initialSearch);
	const [page, setPageState] = createSignal(initialPage);
	const [pageSize, setPageSizeState] = createSignal(initialPageSize);
	const [sort, setSortColumn] = createSignal(initialSort);
	const [asc, setAsc] = createSignal(initialAsc);

	const setSort = (sortColumn: string, defaultAsc?: boolean) => {
		if (sortColumn === sort()) {
			setAsc(!asc());
		} else {
			setSortColumn(sortColumn);
			setAsc(defaultAsc ?? true);
		}
		updateQueryString();
	};
	const updateQueryString = (additionalParams?: Record<string, string>) => {
		const queryString = new URLSearchParams(window.location.search);
		const skSearch = prefixedKey("search");
		const skPage = prefixedKey("page");
		const skPageSize = prefixedKey("pagesize");
		const skSort = prefixedKey("sort");
		const skAsc = prefixedKey("asc");

		if (search() !== "") {
			queryString.set(skSearch, search());
		} else {
			queryString.delete(skSearch);
		}
		if (page() !== defaultPage) {
			queryString.set(skPage, page().toString());
		} else {
			queryString.delete(skPage);
		}
		if (pageSize() !== defaultPageSize) {
			queryString.set(skPageSize, pageSize().toString());
		} else {
			queryString.delete(skPageSize);
		}

		const sortColumn = sort();
		if (defaultSort !== undefined) {
			const effectiveSort = sortColumn ?? defaultSort;
			if (effectiveSort !== defaultSort) {
				queryString.set(skSort, effectiveSort);
			} else {
				queryString.delete(skSort);
			}
		} else if (sortColumn) {
			queryString.set(skSort, sortColumn);
		} else {
			queryString.delete(skSort);
		}

		const ascVal = asc();
		if (urlDefaultAsc !== undefined) {
			const effectiveAsc = ascVal === undefined ? urlDefaultAsc : ascVal;
			if (effectiveAsc !== urlDefaultAsc) {
				queryString.set(skAsc, String(effectiveAsc));
			} else {
				queryString.delete(skAsc);
			}
		} else if (ascVal !== undefined) {
			queryString.set(skAsc, String(ascVal));
		} else {
			queryString.delete(skAsc);
		}

		if (additionalParams) {
			for (const [key, value] of Object.entries(additionalParams)) {
				queryString.set(key, value);
			}
		}
		const qs = queryString.toString();
		window.history.pushState({}, "", qs ? `?${qs}` : window.location.pathname);

		// Call optional update callback
		onUpdate?.({
			search: search(),
			page: page(),
			pageSize: pageSize(),
		});
	};

	const { handleSearch, handleClear, search, setSearch } = useSearch({
		debounceMs,
		onUpdate: updateQueryString,
		defaultSearch: initialSearch,
	});

	const setPage = (value: number) => {
		setPageState(value);
		updateQueryString({ [pageKey]: value.toString() });
	};

	const setPageSize = (value: number) => {
		setPageSizeState(value);
		setPageState(1);
		updateQueryString();
	};

	const getQueryState = () => ({
		search: search(),
		page: page(),
		pageSize: pageSize(),
		page_size: pageSize(),
	});

	return {
		setSearch,
		searchInput,
		setSearchInput,
		page,
		setPage,
		pageSize,
		setPageSize,
		updateQueryString,
		getQueryState,
		searchProps: {
			get value() {
				return search();
			},
			onSearch: (search: string) => {
				handleSearch(search);
				setPage(1);
			},
			onClear: () => {
				handleClear();
				setSearchInput("");
				setPageState(1);
				updateQueryString();
			},
			getQueryState: getQueryState,
		},
		paginationProps: {
			page,
			pageSize,
			onPageChange: setPage,
			onItemsPerPageChange: setPageSize,
		},
		sortProps: {
			sort,
			asc,
			setSort,
		},
	};
}

export const useSearch = ({
	debounceMs = 300,
	defaultSearch = "",
	onUpdate,
}: {
	debounceMs?: number;
	defaultSearch?: string;
	onUpdate?: () => void;
}) => {
	// Debounce timer for search
	let searchDebounceTimer: number | null = null;
	const [search, setSearch] = createSignal(defaultSearch);
	const [searchInput, setSearchInput] = createSignal("");

	const handleSearch = (_search: string) => {
		setSearchInput(_search);

		if (searchDebounceTimer !== null) {
			clearTimeout(searchDebounceTimer);
		}

		searchDebounceTimer = window.setTimeout(() => {
			setSearch(_search);
			onUpdate?.();
			searchDebounceTimer = null;
		}, debounceMs);
	};

	const handleClear = () => {
		if (searchDebounceTimer !== null) {
			clearTimeout(searchDebounceTimer);
			searchDebounceTimer = null;
		}
		setSearchInput("");
		setSearch("");
		onUpdate?.();
	};

	return {
		handleSearch,
		handleClear,
		search,
		setSearch,
		searchInput,
		setSearchInput,
	};
};
